/**
 * Module-level store for "which tenant is the user actively on right
 * now." Populated by AuthBootstrap as soon as the session resolves
 * (via GET /me/tenants), and re-populated whenever the user switches
 * via TenantSwitcher (which also triggers a hard reload so the whole
 * SPA re-initializes anyway).
 *
 * Why module-level (not React context):
 *   * react-query's queryKeyHashFn fires during cache reads inside
 *     useQuery — outside the React render tree. A hook can't reach
 *     it. A module-level variable can.
 *   * The active tenant is, in practice, write-once per page load:
 *     the switcher reloads on change, so we don't need React's
 *     reactivity here.
 *
 * The hash function reads the current value and prepends it as the
 * key's first segment. Two tenants viewing the same screen get
 * partitioned cache entries automatically; cross-tenant cache hits
 * become misses (correctly).
 */

import { getActiveScopePath } from './active-scope.js';

let activeTenantId: string | null = null;

export function setActiveTenantId(id: string | null): void {
  activeTenantId = id;
}

export function getActiveTenantId(): string | null {
  return activeTenantId;
}

/**
 * Drop-in replacement for react-query's default hashFn. Prefixes
 * every cache key with the active tenant id so the cache is
 * naturally partitioned by tenant. Falls back to a `_` placeholder
 * when the tenant hasn't resolved yet (the first paint, before
 * AuthBootstrap finishes its /me/tenants call).
 *
 * ⚠️ CSAGE-364 — this function is NOT pure, and that has a consequence every
 * caller of `setActiveTenantId` / `setActiveScopePath` must honour. react-query
 * computes a cache entry's `queryHash` once, at creation, and stores it on the
 * entry; observers recompute it from the live values on each render. So the
 * instant either module variable changes, every existing entry becomes
 * unaddressable — no observer will ever compute its old hash again.
 *
 * `invalidateQueries()` therefore CANNOT clean up after such a change: it marks
 * entries stale but never evicts them, leaving orphans that (a) pin memory for
 * the page's lifetime and (b) get re-attached and repainted — stale data, or a
 * cached ERROR — if the identity ever returns to a previous value, which the
 * portal's org-tree selector does routinely. The correct call after changing
 * either value is `removeQueries()`. Both call sites in AuthBootstrap do that;
 * this note exists so a third one doesn't reintroduce the bug.
 *
 * The properly pure alternative is to put tenant and scope in the queryKey
 * itself and drop the custom hashFn. That is a wider change — every useQuery in
 * the app grows two key segments — and is left as follow-up.
 */
export function tenantScopedHashFn(queryKey: ReadonlyArray<unknown>): string {
  const prefix = activeTenantId ?? '_';
  // Also partition by the active hierarchy scope so switching the selected
  // customer in the portal yields a distinct cache key (and a refetch),
  // never a stale cross-scope cache hit.
  const scope = getActiveScopePath() ?? '_';
  return JSON.stringify([prefix, scope, ...queryKey]);
}
